"use client";

import { cn } from "@/lib/utils";
import type { FootballTemplateData } from "../football-template";
import { ScorecardShell, type ScorecardVariant } from "./scorecard-shell";

type ScorecardParticipant = {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  username: string | null;
};

type FootballScorecardProps = {
  data: FootballTemplateData | null;
  participants: ScorecardParticipant[];
  variant?: ScorecardVariant;
};

export function FootballScorecard({
  data,
  participants: _participants,
  variant = "feed",
}: FootballScorecardProps) {
  if (!data) return null;

  const { yourGoals, opponentGoals } = data.matchScore;
  const showScore = data.hasScore !== false;
  if (!showScore) return null;

  const yourWon = yourGoals > opponentGoals;
  const opponentWon = opponentGoals > yourGoals;

  const penaltiesWinner = (() => {
    if (!data.penalties) return null;
    if (data.penalties.yourScore === data.penalties.opponentScore) return null;
    return data.penalties.yourScore > data.penalties.opponentScore
      ? "team1"
      : "team2";
  })();

  const resolvedOutcome = (() => {
    if (yourWon) return { team: "team1" as const, via: null as string | null };
    if (opponentWon)
      return { team: "team2" as const, via: null as string | null };
    if (penaltiesWinner)
      return {
        team: penaltiesWinner,
        via: "Pens",
      };
    return { team: "draw" as const, via: null as string | null };
  })();

  const content = (
    <div className={variant === "feed" ? "mb-6" : undefined}>
      <div className="mb-3 flex items-center justify-center gap-6">
        <div className="text-center">
          <div
            className={cn(
              "text-4xl font-bold",
              yourWon ? "text-brand" : "text-text-primary",
            )}
          >
            {yourGoals}
          </div>
          <div className="text-xs text-text-secondary">Team 1</div>
        </div>
        <span className="text-2xl font-bold text-text-secondary">–</span>
        <div className="text-center">
          <div
            className={cn(
              "text-4xl font-bold",
              opponentWon ? "text-error" : "text-text-primary",
            )}
          >
            {opponentGoals}
          </div>
          <div className="text-xs text-text-secondary">Team 2</div>
        </div>
      </div>

      {/* Result badge */}
      <div className="flex justify-center">
        <span
          className={cn(
            "rounded-full px-3 py-1 text-xs font-semibold",
            resolvedOutcome.team === "team1" && "bg-brand/20 text-brand",
            resolvedOutcome.team === "team2" && "bg-error/20 text-error",
            resolvedOutcome.team === "draw" &&
              "bg-text-secondary/20 text-text-secondary",
          )}
        >
          {resolvedOutcome.team === "team1"
            ? `🏆 Team 1 Wins${resolvedOutcome.via ? ` (${resolvedOutcome.via})` : ""}`
            : resolvedOutcome.team === "team2"
              ? `Team 2 Wins${resolvedOutcome.via ? ` (${resolvedOutcome.via})` : ""}`
              : "Draw"}
        </span>
      </div>

      {(data.periods && data.periods.length > 0) || data.penalties ? (
        <div className="mt-3 flex flex-wrap justify-center gap-2">
          {(data.periods ?? []).map((period, i) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: period entries are positional and never reorder
              key={`period-${i}-${period.label}-${period.yourScore}-${period.opponentScore}`}
              className="rounded bg-surface-alt px-2 py-0.5 text-xs font-medium text-text-secondary"
            >
              {period.label} {period.yourScore}–{period.opponentScore}
            </span>
          ))}
          {data.penalties ? (
            <span className="rounded bg-surface-alt px-2 py-0.5 text-xs font-medium text-text-secondary">
              Pens {data.penalties.yourScore}–{data.penalties.opponentScore}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  if (variant === "feed") return content;
  return <ScorecardShell variant={variant}>{content}</ScorecardShell>;
}
